import { useRef } from 'react';
import { useCurrentStateAndParams } from '@uirouter/react';
import clsx from 'clsx';

import { isEnvironmentUrl } from '@/portainer/services/endpointProvider';
import { useChangeConfirmationPolicy } from '@/react/portainer/environments/queries/useChangeConfirmationPolicy';
import { linkifyMessage } from '@/react/components/modals/linkifyMessage';

const FALLBACK_COLOR = '#cccccc';

export function EnvironmentBanner() {
  const { params } = useCurrentStateAndParams();
  const environmentId = isEnvironmentUrl(params.endpointId)
    ? Number(params.endpointId)
    : undefined;

  const policy = useChangeConfirmationPolicy(environmentId);
  const bannerRef = useRef<HTMLDivElement>(null);

  const showBorder = policy?.HighlightEnabled?.value ?? false;
  const color = sanitizeColor(policy?.HighlightColor?.value ?? FALLBACK_COLOR);
  const message = policy?.BannerMessage?.value ?? '';
  const isTruncated = isBannerTruncated();

  return (
    <>
      <style>{`
        #view {
          height: ${message ? 'calc(100vh - 2.75rem)' : '100vh'};
        }
        .change-confirmation-border {
          padding: ${showBorder ? '0.5rem' : '0'};
          background: ${color};
        }
        .change-confirmation-scroll-container {
          border-radius: ${showBorder ? '0.5rem' : '0'};
        }
      `}</style>
      <div
        ref={bannerRef}
        title={isTruncated ? message : undefined}
        className={clsx(
          'w-full overflow-hidden text-ellipsis whitespace-nowrap px-2 text-center text-sm',
          'transition-[height,padding]',
          message ? 'h-auto pb-2' : '!h-0 p-0'
        )}
        style={{
          backgroundColor: color,
          // ensures the text has contrast on the background color
          color: `oklch(from ${color} round(1.21 - L) 0 0)`,
        }}
      >
        {linkifyMessage(message, {
          linkClassName: '!text-inherit underline',
        })}
      </div>
    </>
  );

  function isBannerTruncated() {
    const el = bannerRef.current;
    return el && el.scrollWidth > el.clientWidth;
  }
}

function sanitizeColor(value: string): string {
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
    return value;
  }
  return CSS.supports('color', value) ? value : FALLBACK_COLOR;
}
